import { useEffect, useMemo, useState } from "react";
import { api, type KBFact } from "@/lib/api-client";
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

const parseJson = (value: string) => {
  try {
    JSON.parse(value);
    return { ok: true as const };
  } catch (e) {
    return { ok: false as const, error: e instanceof Error ? e.message : "Invalid JSON" };
  }
};

const statusLabel = (status: KBFact["status"]) => {
  if (status === "filled") return { label: "Filled", variant: "default" as const };
  if (status === "placeholder") return { label: "Placeholder", variant: "secondary" as const };
  return { label: "Missing", variant: "destructive" as const };
};

export function KnowledgeBaseFacts() {
  const [facts, setFacts] = useState<KBFact[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState("");
  const selected = useMemo(() => facts.find((f) => f.key === selectedKey) ?? null, [facts, selectedKey]);

  const [valueJson, setValueJson] = useState("");
  const [tags, setTags] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFacts = async () => {
    setLoading(true);
    try {
      const res = await api.getKBFacts();
      setFacts(res.facts);
      const first = res.facts[0]?.key ?? "";
      setSelectedKey((k) => k || first);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFacts();
  }, []);

  useEffect(() => {
    if (!selected) return;
    setValueJson(selected.valueJson);
    setTags(selected.tags);
    setIsActive(selected.isActive);
    setError("");
  }, [selectedKey, selected]);

  const save = async () => {
    if (!selected) return;
    setError("");
    const parsed = parseJson(valueJson);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    setSaving(true);
    try {
      await api.updateKBFact(selected.key, { valueJson, tags, isActive });
      await loadFacts();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <CardContent className="space-y-4">
      {loading ? (
        <div className="space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 space-y-2">
            <div className="text-xs text-muted-foreground">Facts</div>
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[420px] overflow-auto">
                {facts.map((f) => {
                  const s = statusLabel(f.status);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => setSelectedKey(f.key)}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-muted/50 ${selectedKey === f.key ? "bg-muted" : ""}`}
                    >
                      <span className="text-sm font-mono truncate flex-1">{f.key}</span>
                      <Badge variant={s.variant} className="text-[10px]">
                        {s.label}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium font-mono truncate">{selected?.key}</div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                Active
              </label>
            </div>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags (csv) e.g. pricing,schedule" />
            <Textarea
              value={valueJson}
              onChange={(e) => setValueJson(e.target.value)}
              className="min-h-[280px] font-mono text-sm"
              placeholder='{"status":"placeholder"}'
            />
            {error && <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">{error}</div>}
            <div className="flex justify-end">
              <Button onClick={save} disabled={saving || !selected}>
                {saving ? "Saving…" : "Save Fact"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </CardContent>
  );
}

